// P3 · Armar ruta (documento 07, documento 10). Orden propuesto, optimización y salida.
import { Boton, Chip, cx, Estadistica, Icono, Tarjeta } from '@reparto/ui';
import { formatoGs, hoyISO } from '@reparto/shared/nucleo';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { optimizar, reordenar, salir } from '../datos/acciones';
import { useEstado } from '../datos/ganchos';
import { EsqueletoArmarRuta } from '../componentes/Esqueletos';
import { Marco } from '../componentes/Marco';
import { PanelMapa } from '../componentes/PanelMapa';
import { diaDeCarga, duracion, km, ubicacionCliente, ventana } from '../componentes/formato';
import { usePantallaAncha } from '../componentes/pantalla';

export function ArmarRuta() {
  const navegar = useNavigate();
  const { vista } = useEstado();
  const [saliendo, setSaliendo] = useState(false);
  // En pantallas anchas el mapa queda a la vista al lado de la lista y cambia al reordenar.
  const ancha = usePantallaAncha();
  const ruta = vista?.ruta;
  if (!vista || !ruta) return <EsqueletoArmarRuta />;
  const propone = vista.configuracion.ruta.arma === 'propone';
  const ahorro =
    ruta.optimizada &&
    ruta.km_antes_optimizar &&
    ruta.km_estimados != null &&
    ruta.km_antes_optimizar > ruta.km_estimados
      ? Math.round((1 - ruta.km_estimados / ruta.km_antes_optimizar) * 100)
      : null;
  const sinVerificar = ruta.paradas.filter((p) => ubicacionCliente(p.cliente) !== 'verificada').length;

  const mover = (desde: number, hacia: number) => {
    if (hacia < 0 || hacia >= ruta.paradas.length) return;
    const ids = ruta.paradas.map((p) => p.id);
    [ids[desde], ids[hacia]] = [ids[hacia]!, ids[desde]!];
    void reordenar(ruta.id, ids);
  };

  async function arrancar() {
    setSaliendo(true);
    try {
      await salir(ruta!.id);
      navegar('/ruta', { replace: true });
    } finally {
      setSaliendo(false);
    }
  }

  // Cargado desde la tarde anterior: la ruta es de otro día y se sale recién ese día.
  const otroDia = vista.fecha > hoyISO();
  const pie = (
    <div className="space-y-2 pb-1">
      {propone && !ruta.optimizada ? (
        <Boton bloque variante="acento" tamano="lg" icono="auto_awesome" onClick={() => void optimizar(ruta.id)}>
          Optimizar ruta
        </Boton>
      ) : null}
      {otroDia ? (
        <p className="flex items-center gap-2 rounded-xl bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:bg-amber-900/30 dark:text-amber-100">
          <Icono nombre="event_upcoming" className="shrink-0" />
          Esta ruta es para {diaDeCarga(vista.fecha)}: salís ese día.
        </p>
      ) : null}
      <Boton
        bloque
        tamano="lg"
        icono="local_shipping"
        cargando={saliendo}
        disabled={otroDia}
        onClick={() => void arrancar()}
      >
        Salir a ruta
      </Boton>
    </div>
  );

  return (
    <Marco
      titulo="Armar ruta"
      subtitulo={`${ruta.paradas.length} ${ruta.paradas.length === 1 ? 'parada' : 'paradas'} · ${vista.movil.nombre}`}
      paso="ruta"
      pie={pie}
      lateral={ancha ? <PanelMapa ruta={ruta} /> : undefined}
    >
      <div className="space-y-3 p-4">
        <Tarjeta className="space-y-3">
          <div className="grid grid-cols-3 gap-2 text-center">
            <Estadistica valor={km(ruta.km_estimados)} etiqueta="estimados" />
            <Estadistica valor={duracion(ruta.min_estimados)} etiqueta="con entregas" />
            <Estadistica valor={ruta.resumen.hora_estimada_fin ?? '—'} etiqueta="vuelta aprox." />
          </div>
          {ahorro !== null ? (
            <div className="flex items-center gap-2 rounded-xl bg-primary-50 px-3 py-2 text-sm text-primary-900 dark:bg-primary-900/30 dark:text-primary-100">
              <Icono nombre="trending_down" />
              Antes {km(ruta.km_antes_optimizar)}: ahorrás {ahorro}% del recorrido.
            </div>
          ) : ruta.optimizada ? (
            <div className="flex items-center gap-2 text-sm text-primary-800 dark:text-primary-200">
              <Icono nombre="check_circle" /> Orden optimizado.
            </div>
          ) : null}
          <div className={cx('grid gap-2', !ancha && 'grid-cols-2')}>
            {ancha ? null : (
              <Boton variante="secundario" tamano="sm" icono="map" onClick={() => navegar('/mapa')}>
                Ver mapa
              </Boton>
            )}
            <Boton
              variante="secundario"
              tamano="sm"
              icono="open_in_new"
              disabled={!ruta.enlaces_maps.length}
              onClick={() => window.open(ruta.enlaces_maps[0], '_blank', 'noopener')}
            >
              Google Maps
            </Boton>
          </div>
          {ruta.enlaces_maps.length > 1 ? (
            <div className="flex flex-wrap gap-1 text-xs text-stone-500">
              Tramos:
              {ruta.enlaces_maps.map((u, i) => (
                <a
                  key={u}
                  href={u}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-700 underline dark:text-primary-300"
                >
                  {i + 1}
                </a>
              ))}
            </div>
          ) : null}
        </Tarjeta>

        {sinVerificar ? (
          <p className="flex gap-2 rounded-2xl bg-amber-50 p-3 text-sm text-amber-900 dark:bg-amber-900/30 dark:text-amber-100">
            <Icono nombre="wrong_location" className="shrink-0" />
            {sinVerificar === 1 ? 'Un cliente no tiene' : `${sinVerificar} clientes no tienen`} la ubicación verificada.
            Al entregar se guarda la posición del celular.
          </p>
        ) : null}

        <ol className="space-y-2">
          {ruta.paradas.map((p, i) => {
            const u = ubicacionCliente(p.cliente);
            const v = ventana(p.pedido.ventana_desde, p.pedido.ventana_hasta);
            return (
              <li key={p.id}>
                <Tarjeta relleno="sm" className="flex items-center gap-3">
                  <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-primary-700 text-sm font-bold text-white tabular-nums">
                    {i + 1}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-medium">{p.cliente.nombre}</div>
                    <div className="truncate text-xs text-stone-500 dark:text-stone-400">
                      {p.cliente.zona_nombre ?? 'Sin zona'} · {p.pedido.bultos_total} bultos
                      {p.pedido.cobro !== 'cuenta_corriente'
                        ? ` · ${formatoGs(p.pedido.monto)}`
                        : ' · cuenta corriente'}
                    </div>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {p.llegada_estimada ? <Chip icono="schedule">≈ {p.llegada_estimada}</Chip> : null}
                      {v ? (
                        <Chip
                          tono={p.ventana_en_riesgo ? 'peligro' : 'neutro'}
                          icono={p.ventana_en_riesgo ? 'alarm' : 'event_available'}
                        >
                          {v}
                        </Chip>
                      ) : null}
                      {u === 'aproximada' ? <Chip tono="aviso">Ubicación aproximada</Chip> : null}
                      {u === 'sin' ? <Chip tono="peligro">Sin ubicación</Chip> : null}
                    </div>
                  </div>
                  {propone ? (
                    <div className="flex shrink-0 flex-col">
                      <button
                        type="button"
                        onClick={() => mover(i, i - 1)}
                        disabled={i === 0}
                        aria-label={`Subir ${p.cliente.nombre}`}
                        className={cx(
                          'grid h-10 w-10 place-items-center rounded-full',
                          i === 0
                            ? 'text-stone-300 dark:text-stone-700'
                            : 'active:bg-stone-100 dark:active:bg-stone-800',
                        )}
                      >
                        <Icono nombre="keyboard_arrow_up" />
                      </button>
                      <button
                        type="button"
                        onClick={() => mover(i, i + 1)}
                        disabled={i === ruta.paradas.length - 1}
                        aria-label={`Bajar ${p.cliente.nombre}`}
                        className={cx(
                          'grid h-10 w-10 place-items-center rounded-full',
                          i === ruta.paradas.length - 1
                            ? 'text-stone-300 dark:text-stone-700'
                            : 'active:bg-stone-100 dark:active:bg-stone-800',
                        )}
                      >
                        <Icono nombre="keyboard_arrow_down" />
                      </button>
                    </div>
                  ) : null}
                </Tarjeta>
              </li>
            );
          })}
        </ol>
        {!propone ? (
          <p className="text-center text-xs text-stone-500">
            El orden lo arma el sistema según la configuración de la empresa.
          </p>
        ) : null}
      </div>
    </Marco>
  );
}
